import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

// mui components
import { Box, Stack } from "@mui/material";

// custom components
import CustomLayout from "../../components/layout";
import LoanProgress from "../../components/loan-progress";
import CreateLoanApplication from "../../components/loan-form/create-loan-application";
import LoanAddressApplication from "../../components/loan-form/loan-address-application";
import LoanEmploymentApplication from "../../components/loan-form/loan-employment-application";
import LoanAssetsApplication from "../../components/loan-form/loan-assets-application";
import LoanDemographicApplication from "../../components/loan-form/loan-demographic-application";
import EditLoanApplication from "../../components/loan-form/edit-loan-application";
import CreditReissueApplication from "../../components/loan-form/credit-reissue-application";
import LoanTermsApplication from "../../components/loan-form/create-loan-terms";
import UnderwritingSummary from "../../components/loan-form/underwriting-summary";
import LoanPricingApplication from "../../components/loan-form/loan-pricing-application";
import LoanFeeApplication from "../../components/loan-form/create-loan-fee";
import LoanDocuments from "../../components/loan-form/create-loan-documents";
import Heading5 from "../../components/typography/heading-05";
import useLoanApplicationNavigation from "../../hooks/useLoanApplicationNavigation";

// api slices
import { useGetLoanSummaryQuery } from "../../features/loanSummary/loanSummaryApiSlice";
import { useFieldMutation } from "../../features/field-values/fieldValueApiSlice";
import { useGetLoanMutation } from "../../features/get-loan-data/getLoanApiSlice";

// reducer slices
import {
	selectChildOrganizationName,
	selectOrganizationName,
	selectSelectedOrganization,
	selectUserDetails,
} from "../../features/auth/authSlice";
import { selectImpersonatedUser } from "../../features/admin/impersonated-user/impersonatedUserSlice";

// mui icons
import { CloseOutlined } from "@mui/icons-material";

// custom modals
import ConfirmationModal from "../../components/modal/confirmation-modal";
import NavigationModal from "../../components/modal/navigation-modal";

//helper utils
import { LOAN_CHANNEL_CONSTANTS } from "../../utils/common-constants";
import { selectedUserWithLoanChannel } from "../../utils/common-helpers";
import { VERBIAGE_CONSTANTS } from "../../utils/verbiage.constants";
import {
	EDIT_LOAN_PROGRESS_FIELDS,
	getEditLoanSectionProgress,
} from "../../utils/edit-loan-late-section-progress";

// custom styles
import styles from "./index.module.scss";

const INTERNAL_LOGAN_ROLES = [
	"admin",
	"AE",
	"AM",
	"PA",
	"executive",
	"manager",
	"assistant",
];
const BORROWER_INFORMATION_NAV_INDEX = 0;

const isInternalLoganUser = (user) =>
	INTERNAL_LOGAN_ROLES.some((role) => user?.user_roles?.includes(role));

export const EditLoan = () => {
	const params = useParams();
	const navigate = useNavigate();

	const userDetails = useSelector(selectUserDetails);
	const impersonatedUser = useSelector(selectImpersonatedUser);
	const impersonatedUserRef = useRef(impersonatedUser);
	impersonatedUserRef.current = impersonatedUser;
	const organizationNameValue = useSelector(selectOrganizationName);
	const childOrganizationNameValue = useSelector(selectChildOrganizationName);
	const selectedOrganization = useSelector(selectSelectedOrganization);
	const [field, { isLoading: fetchingValues }] = useFieldMutation();
	const [getLoan] = useGetLoanMutation();

	const [organizationName, setOrganizationName] = useState("");
	const [childOrganizationName, setChildOrganizationName] = useState("");
	const [loanNumber, setLoanNumber] = useState("");

	useEffect(() => {
		if (organizationNameValue !== undefined && organizationNameValue !== "") {
			setOrganizationName(organizationNameValue);
		}
	}, [organizationNameValue]);

	useEffect(() => {
		if (
			childOrganizationNameValue !== undefined &&
			childOrganizationNameValue !== ""
		) {
			setChildOrganizationName(childOrganizationNameValue);
		}
	}, [childOrganizationNameValue]);

	const loanSteps = [
		"Borrower Information",
		"Address",
		"Credit Reissue",
		"Employment and Income",
		"Assets and Liabilities",
		"Declarations and Others",
		"Loan Product and Terms",
		"Underwriting Summary",
		"Loan Pricing",
		`${
			selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.NDC ||
			selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.CORR ||
			selectedUserWithLoanChannel(userDetails, impersonatedUser, selectedOrganization)
				?.loan_channel === LOAN_CHANNEL_CONSTANTS.WHS
				? "Closing Information"
				: "Fees"
		}`,
		"Supporting Documents",
	];

	const [confirmModalText, setConfirmModalText] = useState("");
	const [confirmModalVisible, setConfirmModalVisible] = useState(false);

	const handleOpenConfirmModal = () => {
		setConfirmModalVisible(true);
	};

	const handleCloseConfirmModal = () => {
		setConfirmModalVisible(false);
	};

	const handleConfirmExit = () => {
		navigate(`/home`);
	};

	const handleCloseAdminModal = () => {
		navigate(-1);
	};

	const handleSubmitConfirmModal = () => {
		navigate("/admin");
	};

	const [navigationModalText, setNavigationModalText] = useState("");
	const [navigationModalVisible, setNavigationModalVisible] = useState(false);
	const [orgLoanChannel, setOrgLoanChannel] = useState("");

	const [hideLoan, setHideLoan] = useState(false);

	const handleOpenNavigationModal = () => {
		setNavigationModalVisible(true);
	};

	const handleCloseNavigationModal = () => {
		setNavigationModalVisible(false);
	};

	const handleNavigatePipeline = () => {
		handleCloseNavigationModal();
		navigate("/pipeline");
	};

	const [loanApplicationStage, setLoanApplicationStage] = useState(1);
	const [applicationData, setApplicationData] = useState({});
	const [loanGuid, setLoanGuid] = useState("");

	const {
		handleNavigateToStage,
		makeRegisterForStage,
		incompleteSections,
		completedSections,
		progressCurrentStep,
		preValidateXmlSections,
		resetSectionProgress,
		validateAllSectionsForSubmit,
		markSectionCompletion,
		setCreditProvidedMode,
	} = useLoanApplicationNavigation({
		loanApplicationStage,
		setLoanApplicationStage,
		loanGuid,
	});

	useEffect(() => {
		resetSectionProgress();
	}, [loanGuid, resetSectionProgress]);

	useEffect(() => {
		const initializeSectionCompletion = async () => {
			if (
				!loanGuid ||
				userDetails === undefined ||
				userDetails === null
			) {
				return;
			}

			try {
				const loanData = await getLoan({ loanid: loanGuid }).unwrap();
				const loanApplicationData = loanData?.response;

				if (!loanApplicationData?.applications?.length) {
					return;
				}

				// Seed full loan payload (same shape New Loan keeps in session)
				setApplicationData(loanApplicationData);

				let creditReissueData = "";

				try {
					const fieldValues = await field({
						loan_guid: loanGuid,
						fields: "CX.LC.Credit.Provided",
					}).unwrap();
					creditReissueData = fieldValues?.response?.[0]?.value || "";
				} catch (fieldErr) {
					console.log("credit reissue field fetch error:", fieldErr);
				}

				const dataForValidation = {
					...loanApplicationData,
					creditReissueData,
				};
				const validationOptions = {
					loanOriginatorEmail:
						loanApplicationData?.interviewerEmail ||
						loanApplicationData?.tPO?.lOEmail ||
						"",
					loanProcessorEmail: loanApplicationData?.tPO?.lPEmail || "",
					creditProvided: creditReissueData,
				};

				let progressFieldValues = [];

				try {
					const lateFieldValues = await field({
						loan_guid: loanGuid,
						fields: EDIT_LOAN_PROGRESS_FIELDS,
					}).unwrap();
					progressFieldValues = lateFieldValues?.response || [];
				} catch (lateErr) {
					console.log("edit loan progress field fetch error:", lateErr);
				}

				// Dynamic progress — same section validation approach as Create
				const { completed, incomplete } = getEditLoanSectionProgress(
					dataForValidation,
					progressFieldValues,
					validationOptions
				);

				preValidateXmlSections(dataForValidation, validationOptions);

				completed.forEach((navIndex) => {
					markSectionCompletion(navIndex + 1, true);
				});
				incomplete.forEach((navIndex) => {
					markSectionCompletion(navIndex + 1, false);
				});

				if (creditReissueData) {
					setCreditProvidedMode(creditReissueData);
				}

				const currentImpersonatedUser = impersonatedUserRef.current;
				const effectiveUser = currentImpersonatedUser?.email
					? currentImpersonatedUser
					: userDetails;
				let resumeNavIndex = loanSteps.length - 1;
				if (isInternalLoganUser(effectiveUser)) {
					resumeNavIndex = BORROWER_INFORMATION_NAV_INDEX;
				} else {
				for (let i = 0; i < loanSteps.length; i++) {
					if (incomplete.includes(i) || !completed.includes(i)) {
						resumeNavIndex = i;
						break;
					}
				}
				}
				setLoanApplicationStage(resumeNavIndex + 1);

				// Re-apply after stage-jump effect marks intermediates complete
				setTimeout(() => {
					completed.forEach((navIndex) => {
						markSectionCompletion(navIndex + 1, true);
					});
					incomplete.forEach((navIndex) => {
						markSectionCompletion(navIndex + 1, false);
					});
				}, 0);
			} catch (err) {
				console.log("initializeSectionCompletion error:", err);
			}
		};

		initializeSectionCompletion();
	}, [
		loanGuid,
		userDetails,
		getLoan,
		field,
		preValidateXmlSections,
		markSectionCompletion,
		setCreditProvidedMode,
	]);

	useEffect(() => {
		// check if admin signed in as another user has an organization
		console.log("150 user roles:", userDetails?.user_roles);
		console.log("150 organization name:", organizationName);

		const isImpersonating =
			impersonatedUser?.email !== undefined &&
			impersonatedUser?.email !== null &&
			impersonatedUser?.email !== "";

		if (
			userDetails?.user_roles?.includes("admin") &&
			!isImpersonating &&
			(organizationName === undefined ||
				organizationName === null ||
				organizationName === "")
		) {
			console.log("140 No organization");
			setConfirmModalText(VERBIAGE_CONSTANTS.SIGN_IN_BROKER);
			handleOpenConfirmModal();
		} else {
			console.log("140 organization found:", organizationName);
			handleCloseConfirmModal();
		}
	}, [organizationName, userDetails, impersonatedUser]);

	useEffect(() => {
		// check if user is from the same organization and loan channel
		console.log("145 user channel:", userDetails?.organization_name);
		console.log("145 org loan channel:", orgLoanChannel);

		// Check if admin is impersonating a user
		const isImpersonating = impersonatedUser?.email !== undefined && impersonatedUser?.email !== null && impersonatedUser?.email !== "";
		
		// Use impersonated user details if available, otherwise use current user
		const effectiveUser = isImpersonating ? impersonatedUser : userDetails;
		
		console.log("145 is impersonating:", isImpersonating);
		console.log("145 effective user:", effectiveUser?.email);

		const isInternalUser =
			effectiveUser?.user_roles?.includes("admin") ||
			effectiveUser?.user_roles?.includes("manager") ||
			effectiveUser?.user_roles?.includes("executive") ||
			effectiveUser?.user_roles?.includes("assistant") ||
			effectiveUser?.user_roles?.includes("AE") ||
			effectiveUser?.user_roles?.includes("AM") ||
			effectiveUser?.user_roles?.includes("PA");

		// If admin is impersonating, allow access (inherit mimicked user's permissions)
		if (isImpersonating) {
			console.log("145 admin impersonating user - allowing access");
			return;
		}

		if (isInternalUser) {
			return;
		}

		if (orgLoanChannel !== undefined && orgLoanChannel !== "") {
			console.log("190 current org name:", userDetails?.organization_name);
			console.log("190 loan org name:", orgLoanChannel);

			const userOrgNames = Array.isArray(effectiveUser?.organizations)
				? effectiveUser.organizations.map((org) => org.name)
				: [];
			const primaryOrgName = effectiveUser?.organization_name;

			const hasAccess =
				userOrgNames.includes(orgLoanChannel) ||
				(Array.isArray(primaryOrgName)
					? primaryOrgName.includes(orgLoanChannel)
					: primaryOrgName === orgLoanChannel);

			if (primaryOrgName !== undefined && primaryOrgName !== "" && !hasAccess) {
				console.log("145 organization does not match");
				setNavigationModalText("You do not have access to this loan.");
				handleOpenNavigationModal();
				setHideLoan(true);
			}
		}
	}, [userDetails, orgLoanChannel, impersonatedUser]);

	const findFieldValue = (fieldValues, fieldId) => {
		return fieldValues?.find((o) => o.fieldId === fieldId)?.value;
	};

	const updateField = (fieldValues) => {
		console.log("180 field values:", fieldValues);
		console.log("180 user details:", userDetails);

		setOrgLoanChannel(findFieldValue(fieldValues, "TPO.X14"));

		const isExecutiveOrManager =
			userDetails?.user_roles?.includes("executive") ||
			userDetails?.user_roles?.includes("manager") ||
			userDetails?.user_roles?.includes("AE") ||
			userDetails?.user_roles?.includes("AM") ||
			userDetails?.user_roles?.includes("PA") ||
			userDetails?.user_roles?.includes("assistant");

		console.log("190 is executive or manager:", isExecutiveOrManager);

		const isTpoSecondary = userDetails?.access_level?.includes("TPO Secondary");
		console.log("260 is tpo secondary:", isTpoSecondary);

		if (isExecutiveOrManager || isTpoSecondary) {
			setOrganizationName(findFieldValue(fieldValues, "TPO.X14"));
			setChildOrganizationName(findFieldValue(fieldValues, "TPO.X38"));
		}

	};

	const getFieldValues = async (loanId) => {
		console.log("135 loan id:", loanId);
		try {
			const getValues = await field({
				loan_guid: loanId,
				fields: "TPO.X14,TPO.X38,TPO.X62,TPO.X75",
			}).unwrap();
			console.log("175 all fields:", getValues?.response);
			if (getValues?.response?.length > 0) {
				updateField(getValues?.response);
			}
		} catch (err) {
			console.log("180 error:", err);
		}
	};

	useEffect(() => {
		console.log("215 loan Guid:", loanGuid);
		console.log("215 user details:", userDetails);

		if (
			loanGuid !== undefined &&
			loanGuid !== "" &&
			userDetails !== undefined &&
			userDetails !== null
		) {
			getFieldValues(loanGuid);
		}
	}, [loanGuid, userDetails]);

	const { data: editLoanData } = useGetLoanSummaryQuery({ loanId: params?.id, selectedOrganization, userDetails });
	console.log("32 edit loan data:", editLoanData);
	console.log("32 edit loan number:", editLoanData?.data?.loanNumber);

	useEffect(() => {
		if (editLoanData?.data !== undefined) {
			console.log("230 edit loan data:", editLoanData?.data);
			setLoanNumber(editLoanData?.data?.loanNumber);
		}
	}, [editLoanData]);

	useEffect(() => {
		setLoanGuid(params?.id);
	}, [params]);

	const breadcrumbData = [
		{
			label: "Home",
			path: "/home",
		},
		{
			label: "Loans",
			path: "/pipeline",
		},
		{
			label: loanNumber,
			path: "",
		},
	];

	return (
		<Box>
			<CustomLayout breadcrumb={breadcrumbData} disabledOverflow={true}>
				<Stack direction="row" className={styles.stackContainer}>
					<Stack direction="row" className={styles.bodyContainer}>
						<Stack direction="column" className={styles.loanContainer}>
							{/* page title and close btn container */}
							<Box direction="row" className={styles.headerContainer}>
								<Stack className={styles.headerTextContainer}>
									<Heading5
										text={`Step ${
											loanApplicationStage === 0
												? loanApplicationStage + 1
												: loanApplicationStage
										}: ${
											loanSteps[
												loanApplicationStage === 0
													? loanApplicationStage
													: loanApplicationStage - 1
											]
										}`}
										fontType="semibold"
										className={styles.headerText}
									/>
								</Stack>

								<Stack
									className={styles.closeIconContainer}
									onClick={() => {
										setConfirmModalText(VERBIAGE_CONSTANTS.SURE_EXIT);
										handleOpenConfirmModal();
									}}
								>
									<CloseOutlined className={styles.closeIcon} />
								</Stack>
							</Box>

							{!hideLoan &&
								(loanApplicationStage === 0 ? (
									<CreateLoanApplication
										setLoanApplicationStage={setLoanApplicationStage}
										applicationData={applicationData}
										setApplicationData={setApplicationData}
										setLoanGuid={setLoanGuid}
										loanGuid={loanGuid}
										organizationName={selectedOrganization || organizationName}
										childOrganizationName={childOrganizationName}
										registerSectionHandler={makeRegisterForStage(0)}
										currentStage={1}
									/>
								) : loanApplicationStage === 1 ? (
									<EditLoanApplication
										setLoanApplicationStage={setLoanApplicationStage}
										loanGuid={loanGuid}
										setLoanGuid={setLoanGuid}
										applicationData={applicationData}
										setApplicationData={setApplicationData}
										organizationName={selectedOrganization || organizationName}
										childOrganizationName={childOrganizationName}
										registerSectionHandler={makeRegisterForStage(1)}
										currentStage={1}
									/>
								) : loanApplicationStage === 2 ? (
									<LoanAddressApplication
										setLoanApplicationStage={setLoanApplicationStage}
										loanGuid={loanGuid}
										registerSectionHandler={makeRegisterForStage(2)}
										currentStage={2}
										markSectionCompletion={markSectionCompletion}
										setCreditProvidedMode={setCreditProvidedMode}
									/>
								) : loanApplicationStage === 3 ? (
									<CreditReissueApplication
										setLoanApplicationStage={setLoanApplicationStage}
										setApplicationData={setApplicationData}
										loanGuid={loanGuid}
										registerSectionHandler={makeRegisterForStage(3)}
										currentStage={3}
									/>
								) : loanApplicationStage === 4 ? (
									<LoanEmploymentApplication
										setLoanApplicationStage={setLoanApplicationStage}
										loanGuid={loanGuid}
										registerSectionHandler={makeRegisterForStage(4)}
										currentStage={4}
									/>
								) : loanApplicationStage === 5 ? (
									<LoanAssetsApplication
										setLoanApplicationStage={setLoanApplicationStage}
										loanGuid={loanGuid}
										registerSectionHandler={makeRegisterForStage(5)}
										currentStage={5}
									/>
								) : loanApplicationStage === 6 ? (
									<LoanDemographicApplication
										setLoanApplicationStage={setLoanApplicationStage}
										loanGuid={loanGuid}
										registerSectionHandler={makeRegisterForStage(6)}
										currentStage={6}
									/>
								) : loanApplicationStage === 7 ? (
									<LoanTermsApplication
										setLoanApplicationStage={setLoanApplicationStage}
										loanGuid={loanGuid}
										registerSectionHandler={makeRegisterForStage(7)}
										currentStage={7}
									/>
								) : loanApplicationStage === 8 ? (
									<UnderwritingSummary
										setLoanApplicationStage={setLoanApplicationStage}
										loanGuid={loanGuid}
										registerSectionHandler={makeRegisterForStage(8)}
										currentStage={8}
									/>
								) : loanApplicationStage === 9 ? (
									<LoanPricingApplication
										setLoanApplicationStage={setLoanApplicationStage}
										loanGuid={loanGuid}
										registerSectionHandler={makeRegisterForStage(9)}
										currentStage={9}
										organizationName={selectedOrganization || organizationName}
										childOrganizationName={childOrganizationName}
										incompleteSections={incompleteSections}
										loanSteps={loanSteps}
									/>
								) : loanApplicationStage === 10 ? (
									<LoanFeeApplication
										setLoanApplicationStage={setLoanApplicationStage}
										loanGuid={loanGuid}
										registerSectionHandler={makeRegisterForStage(10)}
										currentStage={10}
									/>
								) : (
									loanApplicationStage === 11 && (
										<LoanDocuments
											setLoanApplicationStage={setLoanApplicationStage}
											loanGuid={loanGuid}
											registerSectionHandler={makeRegisterForStage(11)}
											validateAllSectionsForSubmit={validateAllSectionsForSubmit}
											loanSteps={loanSteps}
											currentStage={11}
											markSectionCompletion={markSectionCompletion}
										/>
									)
								))}
						</Stack>

						{/* application progress */}
						<LoanProgress
							loanSteps={loanSteps}
							currentStep={progressCurrentStep}
							clickNavigation={Boolean(loanGuid)}
							onNavigateToStage={handleNavigateToStage}
							incompleteSections={incompleteSections}
							completedSections={completedSections}
							loanCreated={Boolean(loanGuid)}
						/>
					</Stack>
				</Stack>
			</CustomLayout>

			{/* modals */}
			<ConfirmationModal
				open={confirmModalVisible}
				text={confirmModalText}
				confirmText={
					confirmModalText?.includes("exit") ? "Exit Application" : "Sign In"
				}
				handleConfirm={
					confirmModalText?.includes("exit")
						? handleConfirmExit
						: handleSubmitConfirmModal
				}
				closeText={
					confirmModalText?.includes("exit") ? "Return to Application" : "Close"
				}
				handleClose={
					confirmModalText?.includes("exit")
						? handleCloseConfirmModal
						: handleCloseAdminModal
				}
			/>

			<NavigationModal
				open={navigationModalVisible}
				text={navigationModalText}
				confirmText="Okay"
				handleConfirm={handleNavigatePipeline}
			/>
		</Box>
	);
};

export default EditLoan;
